import React, { useState } from 'react';
import { HelpCircle, ChevronDown, ChevronRight, Radio, Mic, Clock, Wifi, AlertTriangle, CheckCircle } from 'lucide-react';
import PageHeader from '../../components/UI/PageHeader';
import Card from '../../components/UI/Card';

const SECTIONS = [
  {
    icon: Radio,
    title: 'Starting a Gate Session',
    color: 'text-blue-600',
    steps: [
      'Tap Gate Session in the bottom navigation.',
      'If no session is active, tap + Start New Session.',
      'Select your gate from the dropdown and enter the flight number (e.g. SQ321).',
      'Set the departure time — announcements are scheduled relative to this.',
      'Choose the secondary language for bilingual announcements.',
      'Tap Start Session. Audio will be generated in the background.',
      'The system automatically schedules and plays announcements at the correct times.',
    ],
  },
  {
    icon: Clock,
    title: 'Announcement Schedule',
    color: 'text-green-600',
    steps: [
      'T−60 min: Pre-boarding notice.',
      'T−45 min: Boarding call.',
      'T−30 min: Boarding reminder.',
      'T−15 min: Final boarding (plays twice).',
      'T−10 min: Gate closing.',
      'T−5 min: Gate closed.',
      'Each announcement is bilingual (English + secondary language).',
    ],
  },
  {
    icon: Mic,
    title: 'Ad-Hoc Announcements',
    color: 'text-orange-600',
    steps: [
      'Tap Ad-Hoc in the bottom navigation.',
      'Select the target gate.',
      'Use a Quick Template or type your own message.',
      'Tap Broadcast Now — the announcement plays immediately at the gate.',
      'Ad-hoc announcements interrupt any scheduled audio and play right away.',
    ],
  },
  {
    icon: Radio,
    title: 'Pausing & Resuming',
    color: 'text-yellow-600',
    steps: [
      'On the Gate Session screen, tap Pause to halt scheduled announcements.',
      'The gate display shows "paused" — no further scheduled audio plays.',
      'Tap Resume to continue the schedule from where it left off.',
      'Any announcements that were due while paused will not replay.',
    ],
  },
  {
    icon: Wifi,
    title: 'Gate Display (Browser Player)',
    color: 'text-purple-600',
    steps: [
      'Open a browser on the gate display PC and navigate to the Gate Display URL.',
      'Log in with operator credentials.',
      'Tap "Tap to Activate Audio" — required once to unlock the browser audio context.',
      'The page connects automatically and waits for announcements from the server.',
      'Keep this tab open and the screen on during the flight.',
      'The display sends a heartbeat every 30 seconds — supervisors can see it as "online".',
    ],
  },
  {
    icon: AlertTriangle,
    title: 'Troubleshooting',
    color: 'text-red-600',
    steps: [
      'No audio at gate display: ensure "Tap to Activate Audio" was pressed after page load.',
      'Session not found: refresh the Gate Session page — it loads the latest active session.',
      'Audio not generating: check the announcement sequence for "generating" status; wait 30–60 s.',
      'Gate display shows offline: refresh the display page and re-register.',
      'Ad-hoc failed: verify a gate is selected and the server is reachable.',
    ],
  },
  {
    icon: CheckCircle,
    title: 'End of Flight',
    color: 'text-gray-600',
    steps: [
      'The session completes automatically once all scheduled announcements have played.',
      'You can also tap Cancel on the session card to end it early.',
      'Cancelling stops all pending announcements and releases the gate for the next flight.',
      'Start a new session when the next flight is assigned to your gate.',
    ],
  },
];

function Section({ section }) {
  const [open, setOpen] = useState(false);
  const Icon = section.icon;

  return (
    <Card padded={false} className="overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-left"
      >
        <div className="flex items-center gap-3">
          <Icon size={18} className={section.color} />
          <span className="font-semibold text-gray-900 text-sm">{section.title}</span>
        </div>
        {open ? (
          <ChevronDown size={16} className="text-gray-400" />
        ) : (
          <ChevronRight size={16} className="text-gray-400" />
        )}
      </button>

      {open && (
        <div className="px-5 pb-4 border-t border-gray-50">
          <ol className="mt-3 space-y-2">
            {section.steps.map((step, i) => (
              <li key={i} className="flex gap-3 text-sm text-gray-700">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 text-gray-500 text-xs font-bold flex items-center justify-center mt-0.5">
                  {i + 1}
                </span>
                <span className="leading-relaxed">{step}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </Card>
  );
}

export default function StaffHelpPage() {
  return (
    <div className="px-4 py-5 max-w-lg mx-auto">
      <PageHeader
        icon={HelpCircle}
        title="Staff Guide"
        subtitle="Quick reference for operating the Gate PA system"
      />

      <div className="space-y-3">
        {SECTIONS.map(section => (
          <Section key={section.title} section={section} />
        ))}
      </div>

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-2xl px-4 py-4 text-sm text-blue-800">
        <p className="font-semibold mb-1">Need more help?</p>
        <p className="text-blue-700">Contact your supervisor or the system administrator for support.</p>
      </div>
    </div>
  );
}
